<template>
    <v-container fluid class="pa-4">
        <div class="d-flex align-center mb-4">
            <v-btn icon variant="text" size="small" class="mr-2" @click="$router.push('/business-dashboard')">
                <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon color="green" size="28" class="mr-3">mdi-cash-multiple</v-icon>
            <div>
                <h1 class="text-h5 font-weight-bold">Revenue & Billing</h1>
                <p class="text-caption text-medium-emphasis mb-0">Financial performance, collections, and discount impact</p>
            </div>
            <v-spacer/>
            <v-select v-model="months" :items="periodOptions" item-title="label" item-value="value"
                      density="compact" variant="outlined" hide-details style="max-width: 160px" class="mr-2"/>
            <v-btn variant="text" :loading="loading" @click="loadData" prepend-icon="mdi-refresh">Refresh</v-btn>
        </div>

        <v-overlay :model-value="loading && !data" contained class="align-center justify-center">
            <v-progress-circular indeterminate size="48"/>
        </v-overlay>

        <v-alert v-if="error" type="error" variant="tonal" closable class="mb-4" @click:close="error = null">
            {{ error }}
            <template v-slot:append>
                <v-btn variant="text" size="small" @click="loadData">Retry</v-btn>
            </template>
        </v-alert>

        <template v-if="data">
            <!-- Revenue Trend Chart -->
            <v-row dense class="mb-4">
                <v-col cols="12">
                    <v-card elevation="2">
                        <v-card-title class="d-flex align-center py-3">
                            <v-icon color="green" size="20" class="mr-2">mdi-chart-line</v-icon>
                            Monthly Revenue Trend
                            <v-spacer/>
                            <v-chip size="small" color="green" variant="tonal">
                                Collection Rate: {{ data.collection_rate }}%
                            </v-chip>
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Line :data="revenueTrendChartData" :options="lineChartOptions" style="height: 320px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Revenue by Plan & Top Companies -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="purple" size="20" class="mr-2">mdi-chart-donut</v-icon>
                            Revenue by Subscription Plan
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Doughnut v-if="planChartData.datasets[0].data.length > 0"
                                      :data="planChartData" :options="doughnutOptions" style="height: 280px"/>
                            <div v-else class="text-center text-medium-emphasis py-8">No plan revenue data</div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="blue" size="20" class="mr-2">mdi-trophy</v-icon>
                            Top 10 Companies by Revenue
                        </v-card-title>
                        <v-divider/>
                        <v-card-text class="pa-0">
                            <v-table density="compact">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Company</th>
                                        <th class="text-right">Revenue</th>
                                        <th class="text-right">Invoices</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(company, i) in data.top_companies" :key="company.id">
                                        <td>{{ i + 1 }}</td>
                                        <td>{{ company.company_name }}</td>
                                        <td class="text-right font-weight-bold">${{ Number(company.total_revenue).toLocaleString('en-US', {minimumFractionDigits: 2}) }}</td>
                                        <td class="text-right">{{ company.invoice_count }}</td>
                                    </tr>
                                    <tr v-if="data.top_companies.length === 0">
                                        <td colspan="4" class="text-center text-medium-emphasis">No revenue data</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Invoice Aging & Discount Impact -->
            <v-row dense>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="red" size="20" class="mr-2">mdi-clock-alert</v-icon>
                            Invoice Aging (Unpaid)
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="agingChartData" :options="barChartOptions" style="height: 250px"/>
                            <div class="text-center mt-2">
                                <v-chip size="small" color="red" variant="tonal">
                                    Total Outstanding: ${{ totalUnpaid.toLocaleString('en-US', {minimumFractionDigits: 2}) }}
                                </v-chip>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="orange" size="20" class="mr-2">mdi-tag-multiple</v-icon>
                            Discount Impact
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <div v-if="data.discount_impact.by_type.length > 0">
                                <Doughnut :data="discountChartData" :options="doughnutOptions" style="height: 220px"/>
                                <div class="text-center mt-2">
                                    <v-chip size="small" color="orange" variant="tonal">
                                        Total Discounts Given: ${{ Number(data.discount_impact.total_discount).toLocaleString('en-US', {minimumFractionDigits: 2}) }}
                                    </v-chip>
                                </div>
                            </div>
                            <div v-else class="text-center text-medium-emphasis py-8">
                                <v-icon size="48" color="grey-lighten-1">mdi-tag-off</v-icon>
                                <div class="mt-2">No discounts applied in this period</div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {send} from '@/composables/apiClient';
import {Line, Doughnut, Bar} from 'vue-chartjs';
import {Chart, registerables} from 'chart.js';

Chart.register(...registerables);

const loading = ref(false);
const data = ref(null);
const error = ref(null);
const months = ref(12);
const periodOptions = [
    {label: 'Last 6 months', value: 6},
    {label: 'Last 12 months', value: 12},
    {label: 'Last 24 months', value: 24},
    {label: 'Last 36 months', value: 36},
];

const chartColors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#795548', '#607D8B', '#E91E63', '#3F51B5'];

function monthLabel(year, month) {
    return new Date(year, month - 1).toLocaleDateString('en-US', {month: 'short', year: '2-digit'});
}

const revenueTrendChartData = computed(() => {
    if (!data.value?.trend) return {labels: [], datasets: []};
    const trend = data.value.trend;
    return {
        labels: trend.map(r => monthLabel(r.year, r.month)),
        datasets: [
            {
                label: 'Paid Revenue',
                data: trend.map(r => Number(r.paid_revenue)),
                borderColor: '#4CAF50',
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                fill: true,
                tension: 0.3,
            },
            {
                label: 'Unpaid',
                data: trend.map(r => Number(r.unpaid_revenue)),
                borderColor: '#F44336',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                fill: true,
                tension: 0.3,
            },
        ],
    };
});

const planChartData = computed(() => {
    if (!data.value?.by_plan) return {labels: [], datasets: [{data: []}]};
    const plans = data.value.by_plan;
    return {
        labels: plans.map(p => p.plan_name || 'No Plan'),
        datasets: [{
            data: plans.map(p => Number(p.net_revenue)),
            backgroundColor: chartColors.slice(0, plans.length),
        }],
    };
});

const totalUnpaid = computed(() => {
    if (!data.value?.aging) return 0;
    const a = data.value.aging;
    return Number(a.current) + Number(a['30_60_days']) + Number(a['60_90_days']) + Number(a.over_90_days);
});

const agingChartData = computed(() => {
    if (!data.value?.aging) return {labels: [], datasets: [{data: []}]};
    const a = data.value.aging;
    return {
        labels: ['Current (<30d)', '30-60 days', '60-90 days', '90+ days'],
        datasets: [{
            label: 'Outstanding Amount',
            data: [Number(a.current), Number(a['30_60_days']), Number(a['60_90_days']), Number(a.over_90_days)],
            backgroundColor: ['#FFC107', '#FF9800', '#FF5722', '#F44336'],
        }],
    };
});

const discountChartData = computed(() => {
    if (!data.value?.discount_impact?.by_type) return {labels: [], datasets: [{data: []}]};
    const types = data.value.discount_impact.by_type;
    const typeLabels = {
        percent_off: 'Percent Off',
        fixed_monthly: 'Fixed Monthly',
        account_credit: 'Account Credit',
        free_months: 'Free Months',
        fixed_one_time: 'Fixed One-Time',
    };
    return {
        labels: types.map(t => typeLabels[t.discount_type] || t.discount_type),
        datasets: [{
            data: types.map(t => Number(t.total_discount)),
            backgroundColor: chartColors.slice(0, types.length),
        }],
    };
});

const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {position: 'top'},
        tooltip: {
            callbacks: {
                label: (ctx) => ctx.dataset.label + ': $' + ctx.parsed.y.toLocaleString('en-US', {minimumFractionDigits: 2}),
            },
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: {callback: (v) => '$' + v.toLocaleString()},
        },
    },
};

const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {display: false},
        tooltip: {
            callbacks: {
                label: (ctx) => '$' + ctx.parsed.y.toLocaleString('en-US', {minimumFractionDigits: 2}),
            },
        },
    },
    scales: {
        y: {
            beginAtZero: true,
            ticks: {callback: (v) => '$' + v.toLocaleString()},
        },
    },
};

const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {position: 'right'},
        tooltip: {
            callbacks: {
                label: (ctx) => ctx.label + ': $' + ctx.parsed.toLocaleString('en-US', {minimumFractionDigits: 2}),
            },
        },
    },
};

async function loadData() {
    loading.value = true;
    error.value = null;
    try {
        const response = await send('get', 'admin/reports/revenue', {months: months.value});
        if (response?.data?.data) {
            data.value = response.data.data;
        } else {
            error.value = response?.data?.message || 'Failed to load data';
        }
    } catch (e) {
        console.error('Failed to load revenue data', e);
        error.value = e?.response?.data?.message || e?.message || 'Failed to load data';
    } finally {
        loading.value = false;
    }
}

watch(months, () => loadData());
onMounted(loadData);
</script>
